<script setup lang="ts">

import { BadgeOrderStatus } from '@shared/ui/badges'
import { AppText } from '@shared/ui/typography'
import { LinkTextPrimary } from '@shared/ui/links'
import { CardAdmin } from '@shared/ui/cards'

import { useOrderInfo } from '../model/order-info.hook'

defineOptions({
  name: 'SectionOrderInfo',
})

const {
  refOrder,
  refPaymentType,
  refPaymentNumber,
  refDiscount,
  refTotal,
  refCreditsUsed,
  refCustomerText,
  refCustomerUrl,
  refOtherRows,
  formatPrice,
} = useOrderInfo()
</script>

<template>
  <CardAdmin
    title="Order info"
    fullwidth
  >
    <div
      v-if="refOrder"
      class="section-order-info"
    >
      <div class="section-order-info__general">
        <div class="section-order-info__group section-order-info__group--main">
          <div class="section-order-info__row">
            <AppText name="body1-medium">
              Payment type:
            </AppText>
            <AppText
              class="section-order-info__value"
              name="body1-medium"
            >
              {{ refPaymentType }}
            </AppText>
          </div>
          <div
            v-if="refPaymentNumber"
            class="section-order-info__row"
          >
            <AppText name="body1-medium">
              Payment number:
            </AppText>
            <AppText
              class="section-order-info__value"
              name="body1-medium"
            >
              {{ refPaymentNumber }}
            </AppText>
          </div>
          <div class="section-order-info__row">
            <AppText name="body1-medium">
              Order status:
            </AppText>
            <BadgeOrderStatus :status="refOrder.status" />
          </div>
          <div
            v-if="refDiscount"
            class="section-order-info__row"
          >
            <AppText name="body1-medium">
              Discount:
            </AppText>
            <AppText
              class="section-order-info__value"
              name="body1-medium"
            >
              {{ refDiscount }}
            </AppText>
          </div>
          <div class="section-order-info__row">
            <AppText name="body1-medium">
              Total amount:
            </AppText>
            <AppText
              class="section-order-info__value"
              name="body1-medium"
            >
              {{ refTotal }}
            </AppText>
          </div>
          <div class="section-order-info__row">
            <AppText name="body1-medium">
              Credits used:
            </AppText>
            <AppText
              class="section-order-info__value"
              name="body1-medium"
            >
              {{ refCreditsUsed }}
            </AppText>
          </div>
          <div class="section-order-info__row">
            <AppText name="body1-medium">
              Customer:
            </AppText>
            <LinkTextPrimary
              v-if="refCustomerUrl"
              class="section-order-info__value"
              :href="refCustomerUrl"
            >
              {{ refCustomerText }}
            </LinkTextPrimary>
          </div>
        </div>

        <div class="section-order-info__separator" />

        <div class="section-order-info__group">
          <div
            v-for="row in refOtherRows"
            :key="row.label"
            class="section-order-info__row"
          >
            <AppText name="body1-medium">
              {{ row.label }}
            </AppText>
            <AppText
              class="section-order-info__value"
              name="body1-medium"
            >
              {{ row.value }}
            </AppText>
          </div>
        </div>
      </div>

      <div class="section-order-info__items">
        <div class="section-order-info__table">
          <div class="section-order-info__cell section-order-info__cell--head">
            <AppText
              class="section-order-info__value"
              name="body1-medium"
            >
              Item name
            </AppText>
          </div>
          <div class="section-order-info__cell section-order-info__cell--head">
            <AppText
              class="section-order-info__value"
              name="body1-medium"
            >
              Price
            </AppText>
          </div>
          <template
            v-for="item in refOrder.items"
            :key="item.id"
          >
            <div class="section-order-info__cell">
              <AppText name="body1-medium">
                {{ item.product?.name }}
              </AppText>
            </div>
            <div class="section-order-info__cell">
              <AppText name="body1-medium">
                {{ formatPrice(item.price) }}
              </AppText>
            </div>
          </template>
        </div>
        <AppText
          class="section-order-info__total section-order-info__value"
          name="body1-medium"
        >
          Total amount: {{ refTotal }}
        </AppText>
      </div>
    </div>
  </CardAdmin>
</template>

<style scoped lang="scss" src="./SectionOrderInfo.scss" />
